<template>
  <div class="media-page page-ind-kpi min-h-screen w-full p-3 md:p-4">

    <!-- ── Toolbar ── -->
    <section class="ind-toolbar mb-4">
      <div class="ind-toolbar-left">
        <div class="ind-toolbar-field">
          <label class="field-label block mb-1">Buscar</label>
          <div class="kpi-inline-controls-search-input">
            <i class="pi pi-search etq-search-icon"></i>
            <InputText
              v-model="searchQuery"
              placeholder="Device ID o etiqueta…"
              class="etq-search-input kpi-table-search kpi-table-search-with-icon"
            />
          </div>
        </div>
      </div>
      <div class="ind-toolbar-right">
        <Button
          label="Nueva etiqueta"
          icon="pi pi-plus"
          class="p-button-success p-button-sm"
          @click="openNewDialog"
        />
      </div>
    </section>

    <div class="kpi-page-content">
      <!-- ── Cargando (skeleton) ── -->
      <section v-if="loading" class="kpi-section mb-5 kpi-skel-fade-in">
        <div class="etq-skel">
          <div class="etq-skel-row">
            <div class="etq-skel-cell w-1/3"></div>
            <div class="etq-skel-cell w-1/4"></div>
            <div class="etq-skel-cell w-1/5"></div>
            <div class="etq-skel-cell w-16"></div>
          </div>
          <div class="etq-skel-row">
            <div class="etq-skel-cell w-2/5"></div>
            <div class="etq-skel-cell w-1/3"></div>
            <div class="etq-skel-cell w-1/4"></div>
            <div class="etq-skel-cell w-16"></div>
          </div>
          <div class="etq-skel-row">
            <div class="etq-skel-cell w-1/4"></div>
            <div class="etq-skel-cell w-2/5"></div>
            <div class="etq-skel-cell w-1/5"></div>
            <div class="etq-skel-cell w-16"></div>
          </div>
          <div class="etq-skel-row">
            <div class="etq-skel-cell w-1/3"></div>
            <div class="etq-skel-cell w-1/4"></div>
            <div class="etq-skel-cell w-2/5"></div>
            <div class="etq-skel-cell w-16"></div>
          </div>
          <div class="etq-skel-row">
            <div class="etq-skel-cell w-2/5"></div>
            <div class="etq-skel-cell w-1/5"></div>
            <div class="etq-skel-cell w-1/3"></div>
            <div class="etq-skel-cell w-16"></div>
          </div>
        </div>
      </section>

      <!-- ── Vacío ── -->
      <section v-else-if="labels.length === 0 && !loading" class="kpi-section mb-5">
        <div class="etq-empty">
          <i class="pi pi-tag etq-empty-icon"></i>
          <p class="etq-empty-title">No hay etiquetas registradas</p>
          <p class="etq-empty-subtitle">Asigna etiquetas a dispositivos para identificarlos fácilmente desde el dashboard de conectados.</p>
          <Button label="Crear primera etiqueta" icon="pi pi-plus" class="p-button-sm" @click="openNewDialog" />
        </div>
      </section>

      <!-- ── Sin resultados de búsqueda ── -->
      <section v-else-if="filteredLabels.length === 0 && searchQuery" class="kpi-section mb-5">
        <div class="etq-empty">
          <i class="pi pi-search etq-empty-icon etq-empty-icon--sm"></i>
          <p class="etq-empty-title">Sin resultados</p>
          <p class="etq-empty-subtitle">No se encontraron etiquetas que coincidan con "{{ searchQuery }}"</p>
          <Button label="Limpiar búsqueda" icon="pi pi-times" class="p-button-sm p-button-outlined" @click="searchQuery = ''" />
        </div>
      </section>

      <!-- ── Tabla ── -->
      <section v-else class="kpi-section mb-5">
        <h3 class="etq-section-title">
          <i class="pi pi-tag etq-section-icon"></i>
          Etiquetas de dispositivos
        </h3>

        <div class="etq-table-count">
          <span>{{ filteredLabels.length }} etiqueta{{ filteredLabels.length !== 1 ? 's' : '' }}</span>
        </div>

        <DataTable
          :value="filteredLabels"
          stripedRows
          responsiveLayout="scroll"
          class="text-sm kpi-spots-table"
          :paginator="filteredLabels.length > 15"
          :rows="15"
          :rowsPerPageOptions="[15, 30, 50]"
        >
          <Column field="device_id" header="Device ID" :sortable="true" style="min-width:200px">
            <template #body="{ data: row }">
              <span class="etq-cell-device-id">{{ row.device_id }}</span>
            </template>
          </Column>
          <Column field="label" header="Etiqueta" :sortable="true" style="min-width:160px">
            <template #body="{ data: row }">
              <span class="etq-cell-label">{{ row.label }}</span>
            </template>
          </Column>
          <Column field="created_at" header="Creada" :sortable="true" style="width:160px">
            <template #body="{ data: row }">
              <span class="etq-cell-date">{{ formatDate(row.created_at) }}</span>
            </template>
          </Column>
          <Column header="" style="width:90px">
            <template #body="{ data: row }">
              <div class="etq-cell-actions">
                <Button
                  icon="pi pi-pencil"
                  class="p-button-rounded p-button-text p-button-sm etq-btn-icon etq-btn-edit"
                  @click="openEditDialog(row)"
                  v-tooltip.top="'Editar etiqueta'"
                />
                <Button
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-text p-button-sm etq-btn-icon etq-btn-delete"
                  @click="confirmDelete(row)"
                  v-tooltip.top="'Eliminar etiqueta'"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </section>
    </div>

    <!-- ── Dialog nueva/editar ── -->
    <Dialog
      v-model:visible="showDialog"
      modal
      :header="editingLabel ? 'Editar etiqueta' : 'Nueva etiqueta'"
      :style="{ width: '480px' }"
      :dismissableMask="true"
      @hide="cancelDialog"
      class="etq-dialog"
    >
      <div class="etq-dialog-body">
        <div class="etq-dialog-field">
          <label class="etq-dialog-label">Device ID</label>
          <InputText
            ref="deviceIdInput"
            v-model="formDeviceId"
            placeholder="Ej: dev_c39a02c3-…"
            class="w-full"
            :disabled="!!editingLabel"
            @keyup.enter="saveLabel"
          />
          <p v-if="!editingLabel" class="etq-dialog-hint">Ingresa el ID completo del dispositivo</p>
        </div>
        <div class="etq-dialog-field">
          <label class="etq-dialog-label">Etiqueta</label>
          <InputText
            ref="labelInput"
            v-model="formLabel"
            placeholder="Ej: Tablet Oficina Vybroo"
            class="w-full"
            maxlength="255"
            @keyup.enter="saveLabel"
          />
          <p class="etq-dialog-hint">Nombre descriptivo para identificar el dispositivo</p>
        </div>
      </div>
      <template #footer>
        <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="cancelDialog" :disabled="saving" />
        <Button
          label="Guardar"
          icon="pi pi-check"
          class="p-button-success"
          @click="saveLabel"
          :disabled="saving || !formDeviceId || !formLabel"
          :loading="saving"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import axiosInstance from '../axiosInstance';

export default {
    name: 'DispositivosEtiquetas',
    emits: ['update-loading'],
    data() {
        return {
            labels: [],
            loading: false,
            saving: false,
            searchQuery: '',
            showDialog: false,
            editingLabel: null,
            formDeviceId: '',
            formLabel: '',
        };
    },
    computed: {
        filteredLabels() {
            const items = Array.isArray(this.labels) ? this.labels : [];
            if (!this.searchQuery) return items;
            const q = this.searchQuery.toLowerCase().trim();
            return items.filter(l =>
                l.device_id.toLowerCase().includes(q) ||
                l.label.toLowerCase().includes(q)
            );
        },
    },
    methods: {
        async fetchLabels() {
            this.loading = true;
            try {
                const res = await axiosInstance.post('/api/device_labels_list', {
                    token: localStorage.getItem('token'),
                });
                if (res.data?.success) {
                    this.labels = res.data.labels || [];
                }
            } catch (e) {
                console.error('Error al cargar etiquetas:', e);
                this.$toast.add({
                    severity: 'error', summary: 'Error',
                    detail: 'Error al cargar etiquetas.', life: 5000, group: 'topr',
                });
            } finally {
                this.loading = false;
            }
        },

        openNewDialog() {
            this.editingLabel = null;
            this.formDeviceId = '';
            this.formLabel = '';
            this.showDialog = true;
            this.$nextTick(() => {
                this.$refs?.deviceIdInput?.$el?.focus?.();
            });
        },

        openEditDialog(label) {
            this.editingLabel = label;
            this.formDeviceId = label.device_id;
            this.formLabel = label.label;
            this.showDialog = true;
            this.$nextTick(() => {
                this.$refs?.labelInput?.$el?.focus?.();
            });
        },

        cancelDialog() {
            this.showDialog = false;
            this.editingLabel = null;
            this.formDeviceId = '';
            this.formLabel = '';
        },

        async saveLabel() {
            if (!this.formDeviceId.trim() || !this.formLabel.trim()) return;
            this.saving = true;
            try {
                const res = await axiosInstance.post('/api/device_label_save', {
                    token: localStorage.getItem('token'),
                    device_id: this.formDeviceId.trim(),
                    label: this.formLabel.trim(),
                });
                if (res.data?.success) {
                    this.$toast.add({
                        severity: 'success', summary: 'Éxito',
                        detail: this.editingLabel ? 'Etiqueta actualizada correctamente.' : 'Etiqueta creada correctamente.',
                        life: 3000, group: 'topr',
                    });
                    this.cancelDialog();
                    await this.fetchLabels();
                } else {
                    this.$toast.add({
                        severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema al guardar la etiqueta.', life: 5000, group: 'topr',
                    });
                }
            } catch (e) {
                console.error('Error al guardar etiqueta:', e);
                const msg = e?.response?.data?.message || 'Error al guardar la etiqueta.';
                this.$toast.add({
                    severity: 'error', summary: 'Error',
                    detail: msg, life: 5000, group: 'topr',
                });
            } finally {
                this.saving = false;
            }
        },

        confirmDelete(label) {
            this.$confirm.require({
                header: 'Eliminar etiqueta',
                message: `¿Eliminar etiqueta "${label.label}"?`,
                icon: 'pi pi-exclamation-triangle',
                rejectLabel: 'Cancelar',
                acceptLabel: 'Eliminar',
                rejectClass: 'p-button-secondary p-button-outlined',
                acceptClass: 'p-button-danger',
                accept: () => this.deleteLabel(label),
            });
        },

        async deleteLabel(label) {
            this.saving = true;
            try {
                const res = await axiosInstance.post('/api/device_label_kill', {
                    token: localStorage.getItem('token'),
                    id: label.id,
                });
                if (res.data?.success) {
                    this.labels = this.labels.filter(l => l.id !== label.id);
                    this.$toast.add({
                        severity: 'success', summary: 'Éxito',
                        detail: 'Etiqueta eliminada correctamente.', life: 3000, group: 'topr',
                    });
                } else {
                    this.$toast.add({
                        severity: 'warn', summary: 'Aviso',
                        detail: 'Hubo un problema al eliminar la etiqueta.', life: 5000, group: 'topr',
                    });
                }
            } catch (e) {
                console.error('Error al eliminar etiqueta:', e);
                this.$toast.add({
                    severity: 'error', summary: 'Error',
                    detail: 'Error al eliminar la etiqueta.', life: 5000, group: 'topr',
                });
            } finally {
                this.saving = false;
            }
        },

        formatDate(dt) {
            if (!dt) return '—';
            const d = new Date(dt);
            return d.toLocaleDateString('es-MX', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        },
    },
    mounted() {
        this.$emit('update-loading', true);
        this.fetchLabels().finally(() => {
            this.$emit('update-loading', false);
        });
    },
};
</script>

<style>
@import "@/assets/media-module-base.css";

/* ── Hero diferenciado para KPI ── */
.page-ind-kpi {
  --media-hero-bg: linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%);
  --media-hero-border: rgba(147, 197, 253, 0.5);
  --media-hero-bg-dark: linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(24, 24, 27, 0.98) 100%);
  --media-hero-border-dark: rgba(59, 130, 246, 0.2);
  max-width: 960px;
  margin: 0 auto;
}

/* ── Toolbar ── */
.page-ind-kpi .ind-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.page-ind-kpi .ind-toolbar-field {
  width: 280px;
  flex-shrink: 0;
}
.page-ind-kpi .ind-toolbar-right {
  display: flex;
  align-items: flex-end;
  padding-bottom: 2px;
}

/* ── Search input (usando InputText de PrimeVue, no raw input) ── */
.etq-search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.76rem;
  color: #94a3b8;
  pointer-events: none;
  z-index: 1;
}
.dark .etq-search-icon {
  color: #71717a;
}

.etq-search-input.p-inputtext {
  width: 100%;
  height: 42px;
  border-radius: 10px;
  padding-left: 2rem;
  font-size: 0.9rem;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 0.96);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.etq-search-input.p-inputtext:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.2);
}
.etq-search-input.p-inputtext::placeholder {
  color: #94a3b8;
}
.dark .etq-search-input.p-inputtext {
  border-color: rgba(113, 113, 122, 0.48);
  background: rgba(24, 24, 27, 0.75);
  color: #e4e4e7;
}
.dark .etq-search-input.p-inputtext:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
}
.dark .etq-search-input.p-inputtext::placeholder {
  color: #71717a;
}

/* ── Content fade-in (mismo patrón que kpi-page-content) ── */
.kpi-page-content {
  animation: kpi-content-fade-in 0.35s ease-out;
}

@keyframes kpi-content-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Skeleton loading ── */
.kpi-skel-fade-in {
  animation: kpi-skel-fade-in 0.4s ease-out;
}

@keyframes kpi-skel-fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

.etq-skel {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
}
.etq-skel-row {
  display: flex;
  gap: 12px;
  height: 20px;
}
.etq-skel-cell {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: etq-shimmer 1.5s ease-in-out infinite;
}
.dark .etq-skel-cell {
  background: linear-gradient(90deg, rgba(39, 39, 42, 0.8) 25%, rgba(63, 63, 70, 0.5) 50%, rgba(39, 39, 42, 0.8) 75%);
  background-size: 200% 100%;
}
@keyframes etq-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Empty state ── */
.etq-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}
.etq-empty-icon {
  font-size: 3rem;
  color: #cbd5e1;
  margin-bottom: 16px;
}
.etq-empty-icon--sm {
  font-size: 2.2rem;
}
.dark .etq-empty-icon {
  color: #52525b;
}
.etq-empty-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-color, #1e293b);
  margin: 0 0 6px;
}
.dark .etq-empty-title {
  color: #e4e4e7;
}
.etq-empty-subtitle {
  font-size: 0.82rem;
  color: #94a3b8;
  margin: 0 0 20px;
  max-width: 340px;
  line-height: 1.4;
}
.dark .etq-empty-subtitle {
  color: #71717a;
}

/* ── Section title (sin inline styles) ── */
.etq-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--text-color, #1e293b);
}
.dark .etq-section-title {
  color: #e4e4e7;
}
.etq-section-icon {
  font-size: 0.92rem;
  color: #3b82f6;
  opacity: 0.85;
}

/* ── Table count (kpi-inline-count pattern) ── */
.etq-table-count {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 3px 0 10px;
  line-height: 1;
  font-size: 0.74rem;
  color: #94a3b8;
  opacity: 0.72;
}
.dark .etq-table-count {
  color: #71717a;
}

/* ═══════════════════════════════════════════
   kpi-spots-table — EXACTAMENTE como operaciones-kpi.vue
   ═══════════════════════════════════════════ */

.kpi-spots-table .p-datatable-thead > tr > th {
  background: #f1f5f9;
  color: #334155;
  border-color: rgba(148, 163, 184, 0.45);
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.6rem 0.85rem !important;
}
.dark .kpi-spots-table .p-datatable-thead > tr > th {
  background: rgba(24, 24, 27, 0.9);
  color: #d4d4d8;
  border-color: rgba(82, 82, 91, 0.65);
}

.kpi-spots-table .p-datatable-tbody > tr > td {
  border-color: rgba(203, 213, 225, 0.85);
  padding: 0.5rem 0.85rem !important;
  font-size: 0.82rem !important;
}
.dark .kpi-spots-table .p-datatable-tbody > tr > td {
  border-color: rgba(82, 82, 91, 0.35);
}

.kpi-spots-table .p-datatable-tbody > tr:hover > td {
  background: rgba(251, 191, 36, 0.08) !important;
}
.dark .kpi-spots-table .p-datatable-tbody > tr:hover > td {
  background: rgba(251, 191, 36, 0.06) !important;
}

.kpi-spots-table .p-paginator {
  padding: 0.4rem;
  border-top: 1px solid rgba(203, 213, 225, 0.85);
  background: transparent;
}
.dark .kpi-spots-table .p-paginator {
  border-color: rgba(82, 82, 91, 0.35);
}

/* ── Cell styles ── */
.etq-cell-device-id {
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  font-size: 0.72rem;
  color: #475569;
  word-break: break-all;
}
.dark .etq-cell-device-id {
  color: #a1a1aa;
}

.etq-cell-label {
  font-weight: 600;
  color: var(--text-color, #1e293b);
}
.dark .etq-cell-label {
  color: #e4e4e7;
}

.etq-cell-date {
  font-size: 0.78rem;
  color: #71717a;
}
.dark .etq-cell-date {
  color: #a1a1aa;
}

/* ── Action buttons ── */
.etq-cell-actions {
  display: flex;
  gap: 4px;
  justify-content: center;
}
.etq-btn-icon {
  width: 28px !important;
  height: 28px !important;
}
.etq-btn-edit {
  color: #3b82f6 !important;
}
.etq-btn-delete {
  color: #ef4444 !important;
}

/* ── Dialog ── */
.etq-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0;
}
.etq-dialog-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.etq-dialog-label {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--text-color, #1e293b);
}
.dark .etq-dialog-label {
  color: #d4d4d8;
}
.etq-dialog-hint {
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 4px 0 0;
}
.dark .etq-dialog-hint {
  color: #71717a;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .page-ind-kpi .ind-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .page-ind-kpi .ind-toolbar-field {
    width: 100%;
  }
  .page-ind-kpi .ind-toolbar-right {
    justify-content: flex-end;
  }
  .etq-search-input.p-inputtext {
    height: 38px;
    font-size: 0.85rem;
  }
}
</style>
